/**
 * 活动短链：基于构建时按环境写入的 activity-short.product.js / activity-short.top.js / activity-short.intl.js
 * webpack 只编进当前打包环境那一份；业务侧仍 import activity-short
 */
import activityShortRoutes from '@/router/router-child/activity-short'

/** nameEn 规范化：trim，空格替换为 - */
export function normalizeActivityNameEn (nameEn) {
  if (nameEn == null) return ''
  return String(nameEn).trim().replace(/\s+/g, '-')
}

/** nameEn -> { path, seasonId, contestId }（meta 为构建时的当前季） */
const shortRouteByNameEn = new Map()
const registeredPaths = new Set()

function initFromStaticRoutes () {
  activityShortRoutes.forEach(route => {
    if (!route || !route.path) return
    const key = normalizeActivityNameEn(route.path.replace(/^\//, ''))
    if (key && !shortRouteByNameEn.has(key)) {
      shortRouteByNameEn.set(key, {
        path: route.path,
        seasonId: route.meta && route.meta.seasonId != null ? String(route.meta.seasonId) : '',
        contestId: route.meta && route.meta.contestId != null ? String(route.meta.contestId) : ''
      })
    }
    if (route.meta) {
      route.meta.activityShort = true
    }
    registeredPaths.add(route.path)
  })
}

initFromStaticRoutes()

function getShortRouteEntry (nameEn) {
  const key = normalizeActivityNameEn(nameEn)
  if (!key) return null
  return shortRouteByNameEn.get(key) || null
}

/** 命中已注册短链时返回 path（如 /CNCC），否则 null */
export function getActivityShortPath (nameEn) {
  const entry = getShortRouteEntry(nameEn)
  return entry ? entry.path : null
}

/** 该活动 nameEn 是否已在 activity-short 中注册 */
export function isActivityShortRegistered (nameEn) {
  return !!getShortRouteEntry(nameEn)
}

/**
 * 是否应走短链：nameEn 已注册，且未传 seasonId 或 seasonId 与 meta 当前季一致
 */
export function shouldUseActivityShortRoute (nameEn, seasonId) {
  const entry = getShortRouteEntry(nameEn)
  if (!entry) return false
  if (seasonId == null || String(seasonId).trim() === '') return true
  return String(seasonId) === entry.seasonId
}

export function isActivityDetailRoute (route) {
  if (!route) return false
  if (route.name === 'GameData') return true
  if (route.meta && route.meta.activityShort) return true
  if (route.path && registeredPaths.has(route.path)) return true
  return false
}

function normalizePayload (nameEnOrPayload, maybeIds) {
  if (nameEnOrPayload && typeof nameEnOrPayload === 'object' && !Array.isArray(nameEnOrPayload)) {
    return { ...nameEnOrPayload }
  }
  return {
    nameEn: nameEnOrPayload,
    contestId: maybeIds && maybeIds.contestId,
    seasonId: maybeIds && maybeIds.seasonId,
    pinSeason: maybeIds && maybeIds.pinSeason
  }
}

/**
 * 同步计算详情路由：
 * 1. nameEn 命中短链 且（未传 seasonId 或 seasonId === meta 当前季）→ 短链无参
 * 2. 否则 → /GameData/xxx?contestId&seasonId
 * 兼容旧签名：getActivityDetailRoute(nameEn, { contestId, seasonId })
 */
export function getActivityDetailRoute (nameEnOrPayload, maybeIds) {
  const payload = normalizePayload(nameEnOrPayload, maybeIds)
  const nameEn = normalizeActivityNameEn(payload.nameEn)
  const contestId = payload.contestId
  const seasonId = payload.seasonId

  if (shouldUseActivityShortRoute(nameEn, seasonId)) {
    const entry = getShortRouteEntry(nameEn)
    return { path: entry.path }
  }

  if (!nameEn) {
    return { path: '/' }
  }

  const query = {}
  if (contestId != null && contestId !== '') query.contestId = String(contestId)
  if (seasonId != null && seasonId !== '') query.seasonId = String(seasonId)
  return {
    path: `/GameData/${nameEn}`,
    query
  }
}

/** 新窗口打开 hash 路由（详情 / 任务壳等） */
export function openHashRouteInNewTab (route) {
  if (!route || !route.path) return route
  const base = window.location.href.split('/#/')[0] + '/#'
  let url = base + route.path
  if (route.query && Object.keys(route.query).length) {
    const qs = Object.keys(route.query)
      .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(route.query[k])}`)
      .join('&')
    url += '?' + qs
  }
  window.open(url)
  return route
}

/** 新窗口打开活动详情（短链优先） */
export function openActivityDetailInNewTab (nameEnOrPayload, maybeIds) {
  return openHashRouteInNewTab(getActivityDetailRoute(nameEnOrPayload, maybeIds))
}

/**
 * 当前页跳转活动详情
 * @param {Vue|import('vue-router').default} vmOrRouter
 */
export function gotoActivityDetail (vmOrRouter, nameEnOrPayload, maybeIds) {
  const route = getActivityDetailRoute(nameEnOrPayload, maybeIds)
  const router = vmOrRouter && vmOrRouter.$router ? vmOrRouter.$router : vmOrRouter
  if (!router || typeof router.push !== 'function') return route
  return router.push(route)
}



// WEBPACK FOOTER //
// ./src/utils/activityShortRoute.js